/**
 * PATH: packages/email-hub/src/feature/compose/composeDockStore.ts
 * FILE_ID: EMAILHUB-FEATURE-COMPOSE-COMPOSEDOCKSTORE-001
 * PURPOSE: Canonical body of this email compose module. Lifted
 *          from apps/<tenant>/src/features/email/compose/composeDockStore.ts
 *          (see docs/architecture/EMAIL_KANBAN_CUTOVER_ARC.md).
 *          Tenants import this via thin re-export shims; edit
 *          THIS file, not the tenant copies.
 * WHY:     Removes operator-visible drift between tenants —
 *          previously every email feature change had to be
 *          mirrored manually in two tenant trees, and concurrent
 *          agents kept letting one side go stale. One canonical
 *          body, both tenants consume from this package.
 * RELATED:
 *   - apps/<tenant>/src/features/email/compose/composeDockStore.ts: thin re-export
 *     shim that points back here.
 */

import { create } from 'zustand'
import { tlsKey } from '../../_internal/utils/tenantStorage'

export type ComposeIntent = 'new' | 'reply' | 'reply_all' | 'forward'

export interface ComposeDockSeed {
  intent: ComposeIntent
  /** The email being replied to / forwarded. Required for
   *  reply/reply_all/forward; null for `new`. */
  intentEmailId: string | null
  /** For `new` only: the per-window draft id. Each Compose click mints
   *  a fresh one so the operator can keep SEVERAL independent "new
   *  message" drafts open at once. Resuming a saved new draft passes
   *  its existing id so the same slot reopens. */
  draftId?: string | null
  /** Optional pre-fills used by mailto-link callers. */
  initialTo?: string[]
  initialSubject?: string
  /** Optional HTML pre-fill for the body. Used by "compose from"
   *  callers (e.g. the Kanban card → email action) that want to seed
   *  the message body with source content. Applied to a FRESH `new`
   *  window's initial body only — never clobbers a typed/resumed draft
   *  (the merge path below preserves the existing window's body). */
  initialBody?: string
}

/** Mint a per-window id for a fresh `new` compose. crypto.randomUUID
 *  when available; a cheap fallback otherwise (test/JSDOM/older). */
export function newDraftId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
  } catch {
    /* fall through */
  }
  return `d-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

export interface ComposeWindow extends ComposeDockSeed {
  /** Unique per window. Derived from intent + intentEmailId so
   *  openOrRestore can find an existing window. */
  id: string
  isMinimized: boolean
  /** Pixel offset from the viewport's bottom-right. Drag handle
   *  updates these. */
  offsetRight: number
  offsetBottom: number
  /** When this window was opened — used for the "newest first"
   *  ordering in the dock strip. */
  openedAt: number
}

// Shared geometry — ComposeDock.tsx renders with these; the clamp
// helpers below keep every stored offset consistent with them.
// BUG-2026-06-10-COMPOSE-DOCK-OVERLAP.
export const DOCK_WIDTH = 380
export const DOCK_HEIGHT = 520
export const DOCK_HEADER_H = 40
/** Single mobile breakpoint for the compose dock. MUST match the
 *  layout's bottom-nav breakpoint (md: 768px) — the old 640px value
 *  left a 640–768px band where desktop floating windows stacked on
 *  top of the MobileDock + bottom bar. */
export const DOCK_MOBILE_BREAKPOINT = 768

/** Pure I → O: clamp a window's bottom-right offsets so the window
 *  stays fully reachable inside the viewport.
 *    - offsetRight keeps the whole window on-screen horizontally
 *      (left edge ≥ 8px, right edge ≤ vw - 8px);
 *    - offsetBottom floors at 0 — the rendered CSS adds the bottom
 *      bars (`--email-hub-bottom-bar-h` + `--mobile-dock-h`) under
 *      it, so 0 already sits ABOVE the nav instead of behind it —
 *      and caps so the drag header can't leave the top edge. */
export function clampOffsets(
  offsetRight: number,
  offsetBottom: number,
  vw: number,
  vh: number,
): { offsetRight: number; offsetBottom: number } {
  const maxRight = Math.max(8, vw - DOCK_WIDTH - 8)
  const maxBottom = Math.max(0, vh - DOCK_HEIGHT - 8)
  return {
    offsetRight: Math.min(Math.max(offsetRight, 8), maxRight),
    offsetBottom: Math.min(Math.max(offsetBottom, 0), maxBottom),
  }
}

function viewportClamp(
  offsetRight: number,
  offsetBottom: number,
): { offsetRight: number; offsetBottom: number } {
  if (typeof window === 'undefined') return { offsetRight, offsetBottom }
  return clampOffsets(
    offsetRight, offsetBottom, window.innerWidth, window.innerHeight,
  )
}

interface ComposeDockState {
  windows: ComposeWindow[]
  /** The compose tab currently shown in the (single) tabbed dock.
   *  Multi-compose is now ONE dock with a tab per window instead of
   *  N overlapping floating mini-windows; `activeId` is the selected
   *  tab. Falls back to the frontmost window when it points at a
   *  closed id. Set by open/openOrRestore/restore/bringToFront and by
   *  an explicit tab click (`setActive`). */
  activeId: string | null
  /** Select a tab (does not change the window's own state besides
   *  marking it the most-recent interaction). */
  setActive: (id: string) => void
  /** Opens a new window with the seed. If a window with the same
   *  intent + id already exists, this RESTORES that window
   *  (un-minimises + brings to front) instead of duplicating. */
  open: (seed: ComposeDockSeed) => string
  /** Convenience: open if closed, restore if minimised. Same as
   *  `open` but explicitly named for callers (Drafts Resume,
   *  Reply buttons) where "show me the form right now" is the
   *  intent. */
  openOrRestore: (seed: ComposeDockSeed) => string
  /** Close one window. Does NOT clear the draftStore slot — that
   *  decision lives in ComposePage (clear on send + on explicit
   *  discard; preserve on benign close). */
  close: (id: string) => void
  closeAll: () => void
  minimize: (id: string) => void
  restore: (id: string) => void
  /** Update drag position. Caller (drag handler) is responsible
   *  for clamping to keep the window on-screen. */
  setPosition: (id: string, offsetRight: number, offsetBottom: number) => void
  /** Bring a window to the front of the strip (drag start, click
   *  on title bar etc). Moves the window to the END of `windows`,
   *  which is the z-order: ComposeDock renders zIndex = base +
   *  array index, so "front" is deterministic (no more re-random
   *  stacking from the old openedAt-derived zIndex). */
  bringToFront: (id: string) => void
  /** Re-clamp every window into the current viewport. Called by
   *  ComposeDock on window resize so a window dragged on a large
   *  monitor can't be stranded off-screen on a laptop. */
  clampAllToViewport: () => void
}

/** Derive the window id from the seed. Same as the draftStore slot
 *  key — keeps the two stores in lockstep so a Reply re-open lands
 *  on the same draft slot. */
export function windowIdForSeed(seed: ComposeDockSeed): string {
  if (seed.intent === 'new') return seed.draftId ? `new:${seed.draftId}` : 'new:new'
  if (!seed.intentEmailId) return 'new:new'
  return `${seed.intent}:${seed.intentEmailId}`
}

/** Human label for a compose intent — shared by the tab strip, the
 *  dock title bar, and the dock's aria-label. */
export function titleForIntent(intent: ComposeIntent): string {
  switch (intent) {
    case 'reply': return 'Reply'
    case 'reply_all': return 'Reply All'
    case 'forward': return 'Forward'
    default: return 'New Message'
  }
}

/** Helper used by callers to build a seed without re-deriving the
 *  intent shape inline everywhere. */
export function seedFromIntent(
  intent: ComposeIntent,
  intentEmailId: string | null,
  extra: {
    initialTo?: string[]
    initialSubject?: string
    initialBody?: string
    draftId?: string | null
  } = {},
): ComposeDockSeed {
  const isNew = intent === 'new'
  return {
    intent,
    intentEmailId: isNew ? null : intentEmailId,
    // A fresh `new` compose mints its own id so clicking Compose again
    // opens a SEPARATE draft instead of restoring the single old one.
    // Resume passes an explicit id via `extra.draftId` to reopen a
    // saved draft's exact slot.
    draftId: isNew ? (extra.draftId ?? newDraftId()) : undefined,
    initialTo: extra.initialTo,
    initialSubject: extra.initialSubject,
    initialBody: extra.initialBody,
  }
}

/** Build a dock seed that reopens the EXACT slot a draft was saved
 *  under (used by the Drafts list "Resume" + cross-device hydrate).
 *  `new:<id>` → new compose with that draftId; `reply:<emailId>` etc.
 *  → the matching intent + source email. */
export function seedFromSlotKey(
  parsed: { intent: ComposeIntent; intentEmailId: string | null; draftId: string | null },
  extra: { initialTo?: string[]; initialSubject?: string } = {},
): ComposeDockSeed {
  return seedFromIntent(parsed.intent, parsed.intentEmailId, {
    ...extra,
    draftId: parsed.draftId ?? undefined,
  })
}

// BUG-0029 — localStorage key is tenant-namespaced via `tlsKey()`.
// Each tenant resolves the prefix from VITE_NOTIFICATION_NS (SAM →
// 'sam' default, newmark → 'newmark'), so both tenants get the SAME
// bytes in this file but the runtime key remains isolated.
//   SAM     resolves to: 'sam.email.dock.windows.v1'      (back-compat)
//   newmark resolves to: 'newmark.email.dock.windows.v1'  (back-compat)
const POSITION_KEY = tlsKey('email.dock.windows.v1')

interface SavedWindowMeta {
  id: string
  offsetRight: number
  offsetBottom: number
  // Kept on the type for forward-compat; not actually used today.
  isMinimized?: boolean
}

function loadSavedPositions(): Record<string, SavedWindowMeta> {
  if (typeof window === 'undefined') return {}
  try {
    const raw = localStorage.getItem(POSITION_KEY)
    if (!raw) return {}
    const arr = JSON.parse(raw) as SavedWindowMeta[]
    const map: Record<string, SavedWindowMeta> = {}
    for (const w of arr) if (w && w.id) map[w.id] = w
    return map
  } catch {
    return {}
  }
}

function persistPosition(id: string, offsetRight: number, offsetBottom: number): void {
  if (typeof window === 'undefined') return
  try {
    const existing = loadSavedPositions()
    existing[id] = { id, offsetRight, offsetBottom, isMinimized: false }
    localStorage.setItem(POSITION_KEY, JSON.stringify(Object.values(existing)))
  } catch {
    // Silent — autopersist is best-effort.
  }
}

/** Default position for a brand-new window. Stack new windows so
 *  they don't overlap the previous one — Gmail places each new
 *  mini 400px to the left of the right edge.
 *  BUG-2026-06-10-COMPOSE-DOCK-OVERLAP: the caller clamps the result
 *  to the viewport, so on narrow screens later windows pin to the
 *  left margin instead of marching off the edge into the corner. */
function defaultPositionForWindowCount(count: number): { offsetRight: number; offsetBottom: number } {
  const GAP = 16
  return {
    offsetRight: 24 + count * (DOCK_WIDTH + GAP),
    offsetBottom: 0,
  }
}

export const useComposeDockStore = create<ComposeDockState>((set, get) => ({
  windows: [],
  activeId: null,
  setActive: (id) => set({ activeId: id }),
  open: (seed) => {
    const id = windowIdForSeed(seed)
    const existing = get().windows.find((w) => w.id === id)
    if (existing) {
      // Restore + bring to front (move to the end of the z-order).
      // BUG-2026-06-11-COMPOSE-SEED-DROPPED: a fresh seed (mailto-style
      // initialTo/initialSubject) used to be discarded by `...existing`,
      // so clicking an email link while a New Message window was open
      // silently restored the OLD empty window. Merge the new seed into
      // the window meta — ComposePage watches the override props and
      // applies them only while the matching field is still pristine,
      // so typed content is never clobbered.
      set((s) => {
        const rest = s.windows.filter((w) => w.id !== id)
        const restored = {
          ...existing,
          initialTo: seed.initialTo ?? existing.initialTo,
          initialSubject: seed.initialSubject ?? existing.initialSubject,
          // Preserve the existing window's body seed — a re-open must
          // never overwrite content already typed into an open draft.
          initialBody: existing.initialBody,
          isMinimized: false,
          openedAt: Date.now(),
        }
        return { windows: [...rest, restored], activeId: id }
      })
      return id
    }
    const saved = loadSavedPositions()[id]
    const defaults = defaultPositionForWindowCount(get().windows.length)
    // Clamp BOTH the persisted position (saved on a 27" monitor,
    // restored on a 13" laptop) and the stacked default (3rd window
    // on a narrow viewport) into the live viewport.
    const pos = viewportClamp(
      saved?.offsetRight ?? defaults.offsetRight,
      saved?.offsetBottom ?? defaults.offsetBottom,
    )
    const next: ComposeWindow = {
      ...seed,
      id,
      isMinimized: false,
      offsetRight: pos.offsetRight,
      offsetBottom: pos.offsetBottom,
      openedAt: Date.now(),
    }
    set((s) => ({ windows: [...s.windows, next], activeId: id }))
    return id
  },
  openOrRestore: (seed) => get().open(seed),
  close: (id) =>
    set((s) => {
      const windows = s.windows.filter((w) => w.id !== id)
      // When the active tab is the one being closed, fall back to the
      // frontmost remaining window (end of the array) — or null when
      // the dock is now empty.
      const activeId =
        s.activeId === id
          ? (windows[windows.length - 1]?.id ?? null)
          : s.activeId
      return { windows, activeId }
    }),
  closeAll: () => {
    set({ windows: [], activeId: null })
    if (typeof window !== 'undefined') {
      // Wipe persisted positions too — closeAll is the "clean slate"
      // semantic (called from page-mode mount, ESC-all, etc.).
      try { localStorage.removeItem(POSITION_KEY) } catch { /* noop */ }
    }
  },
  minimize: (id) =>
    set((s) => ({
      windows: s.windows.map((w) =>
        w.id === id ? { ...w, isMinimized: true } : w,
      ),
    })),
  restore: (id) =>
    set((s) => {
      const target = s.windows.find((w) => w.id === id)
      if (!target) return s
      const rest = s.windows.filter((w) => w.id !== id)
      // Restoring is an interaction — also bring to front + select it.
      return {
        windows: [
          ...rest,
          { ...target, isMinimized: false, openedAt: Date.now() },
        ],
        activeId: id,
      }
    }),
  setPosition: (id, offsetRight, offsetBottom) => {
    persistPosition(id, offsetRight, offsetBottom)
    set((s) => ({
      windows: s.windows.map((w) =>
        w.id === id ? { ...w, offsetRight, offsetBottom } : w,
      ),
    }))
  },
  bringToFront: (id) =>
    set((s) => {
      const target = s.windows.find((w) => w.id === id)
      if (!target) return s
      const rest = s.windows.filter((w) => w.id !== id)
      return {
        windows: [...rest, { ...target, openedAt: Date.now() }],
        activeId: id,
      }
    }),
  clampAllToViewport: () =>
    set((s) => ({
      windows: s.windows.map((w) => {
        const pos = viewportClamp(w.offsetRight, w.offsetBottom)
        if (
          pos.offsetRight === w.offsetRight &&
          pos.offsetBottom === w.offsetBottom
        ) {
          return w
        }
        return { ...w, ...pos }
      }),
    })),
}))
